'use client';

import { useEffect, useMemo, useState, type KeyboardEvent } from 'react';

import { movePaletteIndex, paletteResults } from '@/lib/desk/command-palette';
import type { CommandEntry } from '@/components/dashboard/types';

export function CommandPalette({
  open,
  entries,
  onClose,
  onGo,
}: {
  open: boolean;
  entries: CommandEntry[];
  onClose: () => void;
  onGo: (href: string) => void;
}) {
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const results = useMemo(() => paletteResults(entries, query), [entries, query]);
  const safeIndex = results.length === 0 ? 0 : Math.min(activeIndex, results.length - 1);

  useEffect(() => {
    setActiveIndex(0);
  }, [query, open]);

  function run(entry: CommandEntry | undefined) {
    if (!entry?.href) return;
    onGo(entry.href);
    onClose();
  }

  function onInputKey(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex(movePaletteIndex(safeIndex, results.length, event.key));
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      run(results[safeIndex]);
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
    }
  }

  if (!open) return null;

  let cursor = 0;
  const groups: { name: string; entries: { entry: CommandEntry; index: number }[] }[] = [];
  for (const entry of results) {
    const current = groups[groups.length - 1];
    const item = { entry, index: cursor };
    cursor += 1;
    if (current && current.name === entry.group) current.entries.push(item);
    else groups.push({ name: entry.group, entries: [item] });
  }

  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center bg-black/60 px-4 pt-24" onClick={onClose}>
      <div
        role="dialog"
        aria-label="Command palette"
        className="w-full max-w-lg overflow-hidden rounded-2xl border border-line bg-card shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <input
          autoFocus
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={onInputKey}
          placeholder="Jump to an action, page, or record"
          aria-activedescendant={results[safeIndex] ? `command-${results[safeIndex].id}` : undefined}
          aria-controls="command-results"
          className="w-full border-b border-line bg-transparent px-4 py-3 text-sm text-ink outline-none placeholder:text-muted"
        />
        <ul id="command-results" role="listbox" aria-label="Commands">
          {groups.map((group) => (
            <li key={group.name}>
              <p className="type-table-header px-4 pb-1 pt-3 text-muted">{group.name}</p>
              <ul>
                {group.entries.map(({ entry, index }) => {
                  const selected = index === safeIndex;
                  return (
                    <li key={entry.id}>
                      <button
                        id={`command-${entry.id}`}
                        type="button"
                        role="option"
                        aria-selected={selected}
                        onMouseEnter={() => setActiveIndex(index)}
                        onClick={() => run(entry)}
                        className={`flex w-full items-baseline justify-between gap-3 px-4 py-2.5 text-left text-sm ${selected ? 'bg-shell text-ink' : 'text-ink'}`}
                      >
                        <span>{entry.label}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </li>
          ))}
          {results.length === 0 && <li className="px-4 py-6 text-sm text-muted">No matches.</li>}
        </ul>
      </div>
    </div>
  );
}
